import { Link } from 'react-router'
import { getUserId } from '#app/utils/auth.server.ts'
import { SITE_NAME } from '#app/utils/branding.ts'
import { cn } from '#app/utils/misc.tsx'
import {
	getEnabledTierConfigs,
	type TierConfig,
} from '#app/utils/purchase-policy.server.ts'
import { getActiveTest } from '#app/modules/test-management/index.ts'
import { type Route } from './+types/pricing.ts'

export const meta: Route.MetaFunction = () => [
	{ title: `Pricing - ${SITE_NAME}` },
	{
		name: 'description',
		content: `Most assessments collapse temperament, potential, and character into a flat profile. The ${SITE_NAME} keeps them distinct — producing a living portrait, an ikon.`,
	},
]

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	const tiers = getEnabledTierConfigs()
	const activeTest = await getActiveTest()
	const totalQuestions = activeTest.sections.reduce(
		(sum, section) => sum + section.questions.length,
		0,
	)
	return { userId, tiers, totalQuestions }
}

// ── Single-product focused card ─────────────────────────────────────────

function ProductCard({
	tier,
	totalQuestions,
	isAuthenticated,
}: {
	tier: TierConfig
	totalQuestions: number
	isAuthenticated: boolean
}) {
	const tierDescription = tier.description.replace(
		/^assessment\b/i,
		`${totalQuestions}-question assessment`,
	)
	const checkoutPath = `/checkout/${tier.id}`
	const loginPath = `/login?redirectTo=${encodeURIComponent(checkoutPath)}`

	return (
		<section
			aria-labelledby={`tier-${tier.id}`}
			className={cn(
				'relative rounded-xl border p-8 md:p-10 lg:p-12',
				'bg-cathedral-surface-strong border-cathedral-border',
				'ring-2 ring-gold/40',
			)}
		>
			<div className="flex flex-col items-center text-center">
				<h2
					id={`tier-${tier.id}`}
					className="cathedral-heading text-cathedral-dark text-3xl font-semibold tracking-wide"
				>
					{tier.name}
				</h2>

				<p className="cathedral-body text-cathedral-brown mt-4 max-w-md text-lg leading-relaxed">
					{tierDescription}
				</p>

				<div className="mt-8 flex items-end gap-2">
					<span className="cathedral-heading text-cathedral-gold text-6xl font-bold leading-none">
						${tier.price / 100}
					</span>
					<span className="cathedral-body text-cathedral-brown pb-1 text-base">
						one-time
					</span>
				</div>

				<p className="cathedral-body text-cathedral-brown mt-3 text-base">
					{tier.attemptsIncluded} assessment attempts included
				</p>

				<div className="mt-10 w-full max-w-sm">
					{isAuthenticated ? (
						<Link
							to={checkoutPath}
							className={cn(
								'inline-flex min-h-[48px] w-full items-center justify-center rounded-sm border-2 px-6 py-3.5 text-center',
								'cathedral-heading text-base tracking-[0.15em] uppercase transition-all duration-300',
								'border-cathedral-gold bg-cathedral-bright text-cathedral-deep hover:shadow-[0_0_30px_rgba(255,140,40,0.3)]',
							)}
						>
							Continue to Checkout
						</Link>
					) : (
						<Link
							to={loginPath}
							className={cn(
								'inline-flex min-h-[48px] w-full items-center justify-center rounded-sm border-2 px-6 py-3.5 text-center',
								'cathedral-heading text-base tracking-[0.15em] uppercase transition-all duration-300',
								'border-cathedral-gold bg-cathedral-bright text-cathedral-deep hover:shadow-[0_0_30px_rgba(255,140,40,0.3)]',
							)}
						>
							Get Started
						</Link>
					)}
				</div>
			</div>
		</section>
	)
}

// ── Multi-tier comparison grid (activates when 2+ tiers exist) ──────────

function PricingCard({
	tier,
	totalQuestions,
	isPrimary = false,
	isAuthenticated,
}: {
	tier: TierConfig
	totalQuestions: number
	isPrimary?: boolean
	isAuthenticated: boolean
}) {
	const tierDescription = tier.description.replace(
		/^assessment\b/i,
		`${totalQuestions}-question assessment`,
	)
	const checkoutPath = `/checkout/${tier.id}`
	const loginPath = `/login?redirectTo=${encodeURIComponent(checkoutPath)}`

	return (
		<section
			aria-labelledby={`tier-${tier.id}`}
			className={cn(
				'relative h-full rounded-xl border p-8 md:p-10',
				'bg-cathedral-surface-strong border-cathedral-border',
				'transition-all duration-200 hover:-translate-y-0.5 hover:border-cathedral-gold/40',
				isPrimary && 'ring-2 ring-gold/40',
			)}
		>
			{isPrimary && (
				<div className="absolute -top-3 left-1/2 -translate-x-1/2">
					<span className="rounded-sm border border-cathedral-gold/30 bg-cathedral-surface px-3 py-1 cathedral-heading text-[11px] tracking-[0.14em] text-cathedral-gold uppercase">
						Most Popular
					</span>
				</div>
			)}

			<h2
				id={`tier-${tier.id}`}
				className="cathedral-heading text-cathedral-dark text-3xl font-semibold tracking-wide"
			>
				{tier.name}
			</h2>

			<p className="cathedral-body text-cathedral-brown mt-4 text-xl leading-relaxed">
				{tierDescription}
			</p>

			<div className="mt-8 flex items-end gap-2">
				<span className="cathedral-heading text-cathedral-gold text-5xl font-bold leading-none">
					${tier.price / 100}
				</span>
				<span className="cathedral-body text-cathedral-brown pb-0.5 text-base">one-time</span>
			</div>

			<p className="cathedral-body text-cathedral-brown mt-3 text-base">
				{tier.attemptsIncluded} assessment attempts included
			</p>

			<div className="mt-10">
				{isAuthenticated ? (
					<Link
						to={checkoutPath}
						className={cn(
							'inline-flex min-h-[44px] w-full items-center justify-center rounded-sm border-2 px-4 py-3 text-center',
							'cathedral-heading text-base tracking-[0.15em] uppercase transition-all duration-300',
							isPrimary
								? 'border-cathedral-gold bg-cathedral-bright text-cathedral-deep hover:shadow-[0_0_30px_rgba(255,140,40,0.3)]'
								: 'border-cathedral-gold text-cathedral-gold hover:bg-cathedral-bright/10',
						)}
					>
						Continue to checkout
					</Link>
				) : (
					<Link
						to={loginPath}
						className={cn(
							'inline-flex min-h-[44px] w-full items-center justify-center rounded-sm border-2 px-4 py-3 text-center',
							'cathedral-heading text-base tracking-[0.15em] uppercase transition-all duration-300',
							'border-cathedral-gold text-cathedral-gold hover:bg-cathedral-bright/10',
						)}
					>
						Log in to purchase
					</Link>
				)}
			</div>
		</section>
	)
}

// ── Page ────────────────────────────────────────────────────────────────

export default function PricingRoute({ loaderData }: Route.ComponentProps) {
	const { userId, tiers, totalQuestions } = loaderData
	const isAuthenticated = Boolean(userId)
	const isSingleProduct = tiers.length === 1

	return (
		<main className="min-h-screen px-6 py-16 md:py-24 bg-cathedral-page">
			<div className="mx-auto flex w-full max-w-6xl flex-col gap-14 md:gap-16">
				{/* Header — only show when logged out (logged-in users have the global navbar) */}
				{!isAuthenticated && (
					<div className="flex items-center justify-between gap-4">
						<div className="cathedral-heading text-cathedral-brown text-base tracking-[0.12em] uppercase">
							{SITE_NAME}
						</div>
						<div className="flex items-center gap-3">
							<Link
								to="/login?redirectTo=%2Fpricing"
								className="cathedral-heading text-cathedral-gold hover:text-cathedral-gold-light rounded-sm border border-cathedral-gold/30 px-4 py-2 text-sm tracking-[0.14em] uppercase transition-colors"
							>
								Log In
							</Link>
							<Link
								to="/signup"
								className="cathedral-body text-cathedral-gold hover:text-cathedral-gold-light text-base underline"
							>
								Sign up
							</Link>
						</div>
					</div>
				)}

				<header className="mx-auto max-w-3xl text-center">
					<p className="cathedral-heading text-cathedral-gold text-xs tracking-[0.16em] uppercase">
						A Three-Dimensional Framework
					</p>
					{isSingleProduct ? (
						<>
							<h1 className="cathedral-heading text-cathedral-dark mt-4 text-5xl font-semibold tracking-wide md:text-6xl">
								See Beyond the Surface
							</h1>
							<p className="cathedral-body text-cathedral-brown mt-5 text-xl leading-relaxed md:text-2xl md:leading-relaxed">
								Most assessments give you a composite — your temperament,
								potential, and character all collapsed into a single flat profile.
								The {SITE_NAME} keeps them distinct so you can see yourself in three
								dimensions: how you are, what you carry, and how you were formed.
							</p>
						</>
					) : (
						<>
							<h1 className="cathedral-heading text-cathedral-dark mt-4 text-5xl font-semibold tracking-wide md:text-6xl">
								Choose Your {SITE_NAME} Tier
							</h1>
							<p className="cathedral-body text-cathedral-brown mt-5 text-xl leading-relaxed md:text-2xl md:leading-relaxed">
								Most assessments give you a composite — your temperament,
								potential, and character all collapsed into a single flat profile.
								The {SITE_NAME} keeps them distinct so you can see yourself in three
								dimensions: how you are, what you carry, and how you were formed.
							</p>
						</>
					)}
					<p className="cathedral-body text-cathedral-brown mt-4 text-base">
						{totalQuestions} questions · 20 minutes · AI-interpreted portrait ·
						 Secure checkout powered by Stripe
					</p>
				</header>

				{/* How it works */}
				<section aria-label="How it works" className="mx-auto w-full max-w-4xl">
					<ol className="grid gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-center">
						<li className="rounded-lg border bg-cathedral-card px-5 py-6 text-center">
							<p className="cathedral-heading text-cathedral-gold text-sm tracking-[0.14em] uppercase">
								01 Purchase
							</p>
							<p className="cathedral-body text-cathedral-brown mt-2 text-base">
								Secure checkout with Stripe in under a minute.
							</p>
						</li>
						<li aria-hidden className="cathedral-heading text-cathedral-gold hidden text-xl md:block">
							→
						</li>
						<li className="rounded-lg border bg-cathedral-card px-5 py-6 text-center">
							<p className="cathedral-heading text-cathedral-gold text-sm tracking-[0.14em] uppercase">
								02 Take the Assessment
							</p>
							<p className="cathedral-body text-cathedral-brown mt-2 text-base">
								{totalQuestions} questions across four sections, about 20
								minutes.
							</p>
						</li>
						<li aria-hidden className="cathedral-heading text-cathedral-gold hidden text-xl md:block">
							→
						</li>
						<li className="rounded-lg border bg-cathedral-card px-5 py-6 text-center">
							<p className="cathedral-heading text-cathedral-gold text-sm tracking-[0.14em] uppercase">
								03 Receive Your Portrait
							</p>
							<p className="cathedral-body text-cathedral-brown mt-2 text-base">
								An AI-interpreted narrative — your three-dimensional ikon.
							</p>
						</li>
					</ol>
				</section>

				{/* Divider */}
				<div className="mx-auto flex w-full max-w-xl items-center gap-4" aria-hidden>
					<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
					<div className="bg-cathedral-bright h-2.5 w-2.5 rotate-45" />
					<div className="h-px flex-1 bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
				</div>

				{/* Product card(s) */}
				{isSingleProduct ? (
					// Single product: centered, focused layout
					<div className="mx-auto w-full max-w-lg">
						<ProductCard
							tier={tiers[0]!}
							totalQuestions={totalQuestions}
							isAuthenticated={isAuthenticated}
						/>
					</div>
				) : (
					// Multiple products: comparison grid
					<div
						className={cn(
							'grid gap-6',
							tiers.length === 2 && 'md:grid-cols-2 max-w-3xl mx-auto',
							tiers.length >= 3 && 'md:grid-cols-3',
						)}
						role="list"
						aria-label="Available pricing tiers"
					>
						{tiers.map((tier, index) => (
							<div key={tier.id} role="listitem">
								<PricingCard
									tier={tier}
									totalQuestions={totalQuestions}
									isPrimary={index === 0}
									isAuthenticated={isAuthenticated}
								/>
							</div>
						))}
					</div>
				)}

				{/* Footer */}
				<section className="border-t border-cathedral-gold/20 pt-8 text-center">
					<p className="cathedral-body text-cathedral-brown text-sm md:text-base">
						By continuing to checkout, you agree to our{' '}
						<Link
							to="/tos"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Terms of Service
						</Link>{' '}
						and{' '}
						<Link
							to="/privacy"
							className="text-cathedral-gold hover:text-cathedral-gold-light underline"
						>
							Privacy Policy
						</Link>
						.
					</p>
				</section>
			</div>
		</main>
	)
}
